import React, { memo, useEffect, useRef } from 'react';
import { debounce } from 'lodash-es';

import {
  EVENT_OFFSET_TOP,
  HOURS_IN_DAY,
  MARGIN_BETWEEN_EVENTS,
  TIME_TABLE_OFFSET_TOP,
  TIME_WIDTH,
} from '~/Screens/Calendar/constants';
import { ICalendarOffset } from '~/Screens/Calendar/interfaces';
import { TimeLine } from './components';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { LIGHT_GREY, PLAIN_GREY } from '~/Constants/Colors.constants';
import ResizeObserverPollyfill from 'resize-observer-polyfill';

export const useStyles = makeStyles(
  {
    container: {
      marginTop: TIME_TABLE_OFFSET_TOP,
      position: 'relative',
      pointerEvents: 'none',
    },
    hourBlock: {
      display: 'flex',
      width: '100%',
      height: '64px',
    },
    time: {
      fontSize: FONT_SIZE_SMALL,
      color: PLAIN_GREY,
      lineHeight: '0',
      width: TIME_WIDTH,
      flexShrink: 0,
    },
    content: {
      display: 'flex',
      width: '100%',
      borderTop: `1px solid ${PLAIN_GREY}`,
      position: 'relative',
    },
    line: {
      height: '1px',
      width: '100%',
      backgroundColor: LIGHT_GREY,
      position: 'absolute',
      top: '50%',
    },
  },
  { name: 'CalendarHours' },
);

interface ICalendarHoursProps {
  hasToday: boolean;
  setOffset: React.Dispatch<React.SetStateAction<ICalendarOffset>>;
}

export const CalendarHours = memo(({ setOffset, hasToday }: ICalendarHoursProps) => {
  const { classes } = useStyles();
  const heightRef = useRef<null | HTMLDivElement>(null);

  useEffect((): void | (() => void) => {
    if (!heightRef.current) {
      return;
    }
    const frameWidth = TIME_WIDTH + MARGIN_BETWEEN_EVENTS;
    const { offsetWidth: width, offsetHeight: height } = heightRef.current;

    setOffset((prev: ICalendarOffset) => ({
      ...prev,
      top: TIME_TABLE_OFFSET_TOP + EVENT_OFFSET_TOP,
      left: TIME_WIDTH,
      width: width - frameWidth,
      height,
    }));

    const ro = new ResizeObserverPollyfill(
      debounce((entries) => {
        try {
          for (const entry of entries) {
            const cr = entry.contentRect;
            requestAnimationFrame(() => {
              setOffset((prev: ICalendarOffset) => {
                const newWidth = cr.width - frameWidth;
                return prev.width !== newWidth ? { ...prev, width: newWidth } : prev;
              });
            });
          }
        } catch (error) {
          console.warn('ResizeObserver error:', error);
        }
      }, 100),
    );

    if (heightRef.current) {
      ro.observe(heightRef.current);
    }
    return () => ro.disconnect();
  }, [heightRef]);

  return (
    <div ref={heightRef} className={classes.container}>
      {[...Array(HOURS_IN_DAY)].map((_, index) => (
        <div className={classes.hourBlock} key={index}>
          <div className={classes.time}>{String(index).padStart(2, '0')}:00</div>
          <div className={classes.content}>
            <div className={classes.line} />
          </div>
        </div>
      ))}
      {hasToday && <TimeLine />}
    </div>
  );
});
